import React from 'react';
import { get } from 'lodash-es';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { hasAnchorNavForSubtypePage } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { TypePageDecorator } from 'site-modules/upper-funnel/decorators/type-page-decorator';
import { defineSeoContent } from 'site-modules/upper-funnel/utils/seo-content';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { getMostSoldTypeContentPath } from 'site-modules/shared/utils/upper-funnel/most-sold-type-infographic';
import { getSubtypeContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { HERO_BG_PATHS } from 'site-modules/upper-funnel/components/type/hero-content/constants';
import { PageModel } from 'client/data/models/page';
import { CmsModel } from 'client/data/models/cms';
import { getBestRankings, SEOModel } from 'client/data/models/seo';
import { create404Error } from 'site-modules/shared/utils/errors';

import { BestRankingsPage } from './best-rankings';

function preload(store, isBelowFoldPreload) {
  const preloader = new ModelPreloader(store);

  const state = store.getState();
  const { mobile } = state;

  const rankingsPath = getBestRankings();
  preloader.resolve(getBestRankings(), SEOModel);

  return preloader.load().then(rankingState => {
    const bestRankings = rankingState.get(rankingsPath, SEOModel);
    const rankings = bestRankings[0];

    if (!rankings) {
      throw create404Error('Could not find bestRankings path');
    }

    const type = get(rankings, 'edmundsTypeCategory', '').toLowerCase();
    const subtype = get(rankings, 'subtype', '').toLowerCase();
    const vehicleEditorialRankings = get(rankings, 'segmentRatings');
    const vehicle = { type: { niceName: type } };
    store.dispatch(updateVehicle(vehicle));
    setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);

    if (isBelowFoldPreload) {
      const mostSoldTypeUniqueName = `${subtype}-${type}s`.toLowerCase();
      preloader.resolve(`content["${getMostSoldTypeContentPath(mostSoldTypeUniqueName)}"]`, CmsModel);
      preloader.resolve(`content["${getSubtypeContentPath(type, subtype)}"]`, CmsModel);
    }

    return preloader.load().then(modelState => {
      const siteServedAds = modelState.get(
        getCreativeConfigAdPathByVehicle(mobile, vehicle),
        NativeAdsCreativeConfigModel
      );

      const imageHref = mobile ? HERO_BG_PATHS.sm : HERO_BG_PATHS.lg;
      const assetsToPreload = [{ href: imageHref, as: 'image' }];
      preloader.set('server.preloadedAssets', PageModel, assetsToPreload);

      store.dispatch(
        setCriticalCss({
          type,
          criticalCssHeight: 2000,
          hasAnchorNav: hasAnchorNavForSubtypePage(type, subtype, vehicleEditorialRankings),
          ...siteServedAdsCriticalCss(siteServedAds),
          topRatedPromo: !!modelState.get('top-rated-promo', FeatureFlagModel),
        })
      );
      return preloader.load();
    });
  });
}

export const pageDefinitionConfig = ({ featureFlags }) => ({
  name: 'new_subtype_index',
  category: 'new_subtype_index',
  preload: store => preload(store, true),
  preloadAboveFold: featureFlags?.bestRankingPreloadAboveFold ? store => preload(store) : null,
  component: props => <BestRankingsPage {...props} />,
  decorator: TypePageDecorator,
  seo: (state, props) => {
    const rankings = get(state, 'seo.bestRankings.[0]');
    return defineSeoContent(state, props, get(rankings, 'edmundsTypeCategory'), get(rankings, 'subtype'), rankings);
  },
  options: {
    hasVehicleContext: true,
  },
});

export default params => pageDefinition(pageDefinitionConfig(params));
